import React, { FC, RefObject, ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OverviewType } from "../../../../app";
import { Select } from "antd";
import SelectEmptyState from "../../../../metadata/components/categorization/SelectEmptyState";
import { ColumnItem } from "../../../interfaces/overview";
import { handleKeyEvent } from "../../../utils/keyboardHelper";
import useShowBlankEntriesCheckbox from "../../../hooks/useShowBlankEntriesCheckbox";
import useFilteringSearch from "../../../hooks/useFilteringSearch";
import useApiFilterableItem from "../../../hooks/useApiFilterableItem";
import BlankEntriesCheckbox from "./BlankEntriesCheckbox";
import useTableHeaderFilter from "../../../hooks/useTableHeaderFilter";
import { getKeyFromMetaData } from "../../../utils/columnItemMetaDataHelpers";
import ExtraSelectFilter from "./ExtraSelectFilter";
import DropdownActions from "../../../../common/tableHeaderFilter/dropdownActions/DropdownActions";

type Props = {
  columnItem: ColumnItem;
  label: ReactNode;
  placeholder: string;
  mode?: "multiple";
  renderRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  overviewType: OverviewType;
};

const ApiFilterableItem: FC<Props> = ({
  columnItem,
  label,
  placeholder,
  mode,
  renderRef,
  isOpen,
  overviewType,
}) => {
  const { t } = useTranslation("overview");
  const { getValueFromKey } = useTableHeaderFilter(columnItem, overviewType);

  const values = useMemo(() => {
    return getValueFromKey(
      getKeyFromMetaData(columnItem?.metadata, overviewType),
      columnItem?.metadata?.filterAble!.delimiter,
    )?.split("+");
  }, [getValueFromKey, columnItem?.metadata, overviewType]);

  const {
    options,
    debouncedHandleSearch,
    handleSearch,
    handlePreFetch,
    handleFilter,
    handleBlur,
    searchValue,
  } = useFilteringSearch(
    columnItem?.metadata?.filterAble!.query,
    undefined,
    values,
  );

  const {
    isBlankEntriesChecked,
    handleChangeBlankEntries,
    labelCheckbox,
    showBlankEntriesCheckbox,
    blankValue,
  } = useShowBlankEntriesCheckbox(columnItem, overviewType, values);

  const { ref, selectValue, handleChange, open, setOpen } =
    useApiFilterableItem(
      columnItem,
      overviewType,
      isOpen,
      isBlankEntriesChecked,
      blankValue,
      values,
      searchValue,
      handleSearch,
      mode,
    );

  // When clientSideFilter is enabled, options are filtered locally via filterOption
  // and we skip the per-keystroke API call.
  const isClientSideFilter =
    !!columnItem?.metadata?.filterAble?.query?.clientSideFilter;

  // Select every option currently shown in the dropdown (e.g. after typing
  // "NAU" the user can add all matching certificates in one click) on top of
  // what is already selected.
  const handleSelectAll = useCallback(() => {
    const merged = Array.from(
      new Set([
        ...(selectValue ?? []),
        ...options.map((option) => option.value),
      ]),
    );
    handleChange(merged);
  }, [selectValue, options, handleChange]);

  const showSelectAll = mode === "multiple" && options.length > 0;

  return (
    <div className="filter-popover__item">
      <label className="filter-popover__label">{label}</label>
      {showBlankEntriesCheckbox ? (
        <BlankEntriesCheckbox
          checkboxValue={isBlankEntriesChecked}
          handleChangeBlankEntries={handleChangeBlankEntries}
          labelCheckbox={labelCheckbox}
        />
      ) : null}
      <Select
        ref={ref}
        showSearch
        allowClear
        value={selectValue}
        placeholder={placeholder}
        autoClearSearchValue={mode === "multiple" ? false : undefined}
        onSearch={isClientSideFilter ? undefined : debouncedHandleSearch}
        notFoundContent={<SelectEmptyState />}
        onChange={handleChange}
        onBlur={() => {
          handleBlur();
        }}
        mode={mode}
        filterOption={handleFilter}
        options={options}
        getPopupContainer={() => renderRef.current!}
        onInputKeyDown={handleKeyEvent}
        open={open}
        onDropdownVisibleChange={(open) => setOpen(open)}
        onFocus={() => handlePreFetch()}
        dropdownRender={(menu) => (
          <DropdownActions
            handleOkClick={() => setOpen(false)}
            handleSelectAllClick={showSelectAll ? handleSelectAll : undefined}
            selectAllText={t("filters.selectAll")}
          >
            {menu}
          </DropdownActions>
        )}
      />
      <ExtraSelectFilter
        columnItem={columnItem}
        overviewType={overviewType}
        isOpen={isOpen}
      />
    </div>
  );
};

export default ApiFilterableItem;
